import React, { useEffect, useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { getTrialBannerState } from '../../util/subscriptionsHelpers';
import { NamedLink, TopBanner } from '../../components';

import css from './SubscriptionBanner.module.css';

// One key per banner kind so dismissing "expiring soon" doesn't also hide the
// later "expired" message. sessionStorage means the dismissal lasts for the
// browser session and the banner returns on the next visit.
const DISMISS_KEY_PREFIX = 'subscriptionBannerDismissed:';

const readDismissed = kind => {
  if (typeof window === 'undefined' || !kind) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(DISMISS_KEY_PREFIX + kind) === 'true';
  } catch (e) {
    return false;
  }
};

const rememberDismissed = kind => {
  if (typeof window === 'undefined' || !kind) {
    return;
  }
  try {
    window.sessionStorage.setItem(DISMISS_KEY_PREFIX + kind, 'true');
  } catch (e) {
    // sessionStorage unavailable (private mode / disabled) — dismissal just won't persist.
  }
};

/**
 * Global banner that nudges company/broker users to subscribe.
 *
 * Both states render yellow and are dismissible for the session:
 * - "expiringSoon": trial ends within the threshold.
 * - "expired": trial is over and no subscription exists.
 *
 * Renders nothing for everyone else (paying subscribers, non-trial users, healthy trials).
 */
const SubscriptionBanner = props => {
  const { currentUser } = props;
  const state = getTrialBannerState(currentUser);
  const kind = state?.kind;

  // Seed to false to match server render, then sync from sessionStorage on the
  // client to avoid a hydration mismatch.
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(readDismissed(kind));
  }, [kind]);

  if (!state || dismissed) {
    return null;
  }

  const isExpired = kind === 'expired';

  const handleDismiss = () => {
    rememberDismissed(kind);
    setDismissed(true);
  };

  return (
    <TopBanner type="warning" dismissible onDismiss={handleDismiss} testId="SubscriptionBanner">
      <span className={css.message}>
        {isExpired ? (
          <FormattedMessage id="SubscriptionBanner.expired" />
        ) : (
          <FormattedMessage
            id="SubscriptionBanner.expiringSoon"
            values={{ days: state.daysRemaining }}
          />
        )}
      </span>
      <NamedLink name="Prices" className={css.cta}>
        <FormattedMessage id="SubscriptionBanner.subscribeCta" />
      </NamedLink>
    </TopBanner>
  );
};

export default SubscriptionBanner;
